/**
 * Toán của trình cắt ảnh vuông — **không React, không DOM** (2026-09-23).
 *
 * Tách khỏi `components/media/SquareCropper.tsx` có chủ đích: đây là **chỗ duy nhất trong cả
 * tính năng có thể sai âm thầm**. Sai thì ảnh lệch chứ không ném lỗi ở đâu cả — không phép đo
 * nào của build, typecheck hay audit layout bắt được. Ở một file thuần thì nó chạy thẳng bằng
 * `node` và đối chiếu được với số tính tay.
 *
 * Quy ước toạ độ dùng xuyên suốt:
 *   · `scale`   — số px hiển thị trên 1px ảnh gốc
 *   · `offset`  — lệch của góc trái–trên **ảnh** so với góc trái–trên **khung xem**, px hiển thị.
 *                 Kéo ảnh sang trái ⇒ `offsetX` âm dần.
 *   · `crop`    — px trên ảnh **đã xoay**, đúng thứ `sharp.extract()` ở API cần.
 */

export interface CropValue {
  rotate: 0 | 90 | 180 | 270
  x: number
  y: number
  size: number
}

export interface ViewState {
  scale: number
  offsetX: number
  offsetY: number
  rotate: 0 | 90 | 180 | 270
}

/**
 * Kích thước ảnh **sau khi xoay**. Xoay 90°/270° là đổi chỗ hai cạnh.
 *
 * ⚠️ Quên chỗ này thì mọi phép kẹp biên sai đúng ở hai hướng xoay đó — và sai theo kiểu ảnh
 * lệch chứ không nổ.
 */
export function rotatedSize(w: number, h: number, rotate: number): { w: number; h: number } {
  return rotate === 90 || rotate === 270 ? { w: h, h: w } : { w, h }
}

/** Mức phóng nhỏ nhất còn phủ kín ô vuông — nhỏ hơn là lọt nền trống vào ảnh cắt. */
export function minScale(view: number, w: number, h: number, rotate: number): number {
  const r = rotatedSize(w, h, rotate)
  return view / Math.min(r.w, r.h)
}

/**
 * Kẹp `offset` để ô vuông luôn nằm trọn trong ảnh.
 *
 * Biên trên là 0 (mép ảnh trùng mép khung), biên dưới là `view - canhAnh*scale` (mép kia của
 * ảnh trùng mép kia của khung).
 */
export function clampOffset(offset: number, view: number, sideLen: number, scale: number): number {
  const min = view - sideLen * scale
  return Math.min(0, Math.max(min, offset))
}

/**
 * Khung xem → khung cắt, px trên ảnh **đã xoay**.
 *
 *     size = V / scale          V = cạnh khung xem
 *     x    = -offsetX / scale
 *
 * Làm tròn ở đây chứ không để server làm: server còn kẹp thêm lần nữa, và hai lần làm tròn
 * khác nhau thì số ghi vào DB lệch số đã tính — lần mở sau khung sẽ nhích một chút.
 */
export function viewToCrop(v: ViewState, viewSize: number): CropValue {
  return {
    rotate: v.rotate,
    x: Math.max(0, Math.round(-v.offsetX / v.scale)),
    y: Math.max(0, Math.round(-v.offsetY / v.scale)),
    size: Math.round(viewSize / v.scale),
  }
}

/** Đường ngược lại — mở một khung đã lưu ra để sửa tiếp. */
export function cropToView(crop: CropValue, viewSize: number): ViewState {
  const scale = viewSize / crop.size
  return { scale, offsetX: -crop.x * scale, offsetY: -crop.y * scale, rotate: crop.rotate }
}

/** Khung mặc định cho ảnh vừa chọn: ô vuông lớn nhất, đặt giữa ảnh. */
export function defaultCrop(width: number, height: number): CropValue {
  const size = Math.min(width, height)
  return { rotate: 0, x: Math.round((width - size) / 2), y: Math.round((height - size) / 2), size }
}

/** Đưa ảnh về giữa khung ở mức phóng vừa khít — dùng khi mở lần đầu, xoay, và "Vừa khung". */
export function fitCenter(view: number, w: number, h: number, rotate: 0 | 90 | 180 | 270): ViewState {
  const r = rotatedSize(w, h, rotate)
  const scale = minScale(view, w, h, rotate)
  return { rotate, scale, offsetX: (view - r.w * scale) / 2, offsetY: (view - r.h * scale) / 2 }
}
